/// <amd-module name="Wit/Common/BacklogContent/MoveToPositionDialog" />
import * as React from "react";
import { useState } from "react";
import { format } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { Dialog } from "VSSUI/Dialog";
import { FormItem } from "VSSUI/FormItem";
import { TextField } from "VSSUI/TextField";
import { ITreeItemEx } from "VSSUI/Utilities/TreeItemProvider";
import * as Resources from "Wit/Common/BacklogContent/Resources";
import { IBacklogTreeRow } from "Wit/Common/BacklogContentService/BacklogContentTreeTypes";
import { IBacklogWorkItemService } from "Wit/Common/BacklogContentService/IBacklogWorkItemService";
import { ICommonBacklogContentService } from "Wit/Common/BacklogContentService/ICommonBacklogContentService";
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";

export interface IMoveToPositionProps {
    onDismiss?: () => void;
    onReorder?: (position: number) => void;
    maximumOrder: number;
    pageContext: IVssPageContext;
    selectedItems: ITreeItemEx<IBacklogTreeRow>[];
}

export function MoveToPositionDialog(props: IMoveToPositionProps) {
    const { onReorder, maximumOrder, pageContext, selectedItems } = props;
    const [moving, setMoving] = useState(false);
    const [position, setPosition] = useState("");
    const [isError, setIsError] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    const moveWorkItem = async () => {
        setMoving(true);
        const contentService = pageContext.getService<ICommonBacklogContentService>("ICommonBacklogContentService");
        const backlogTree = contentService.getBacklogTree();
        const pos = Number(position);
        let previousWorkItemId: number | null = null;
        let nextWorkItemId: number | null = null;
        let workItemIds: number[];
        workItemIds = selectedItems
            .filter(item => item.underlyingItem.data.value.order)
            .map(item => item.underlyingItem.data.value.coreColumnMap[CoreFieldRefNames.Id] as number)
            .filter(id => id);

        // For move to position we take the top-level items (only these have an order number), which are *not selected* (because selected
        // items might move, shifting order numbers). Then we try to find the correct reference element in that set.
        // Example:
        // Vis.Order  Id  Selected
        //    1       A    x
        //    2       B
        //    3       C    x
        //    4       D
        // We try to move the selection to position 3. Filtered top level items are {B, D} (the selected items will move, so cannot be
        // considered for the selection of the reference element). Since we intend to move selection to 3, D has to be the previous-reference
        // element for the move operation. The result will be:
        // Vis.Order  Id  Selected
        //    1       B
        //    2       D
        //    3       A    x
        //    4       C    x
        //
        // Note: We will never be "reparenting" because this option only exists at the top level
        const workItemIdsMap: Map<number, boolean> = new Map();
        workItemIds.forEach(id => workItemIdsMap.set(id, true));
        const topLevelItemsAfterSelectionIsMoved = backlogTree.value?.rows
            .filter(treeItem => {
                const id = treeItem.data.value.coreColumnMap[CoreFieldRefNames.Id];
                return treeItem.data.value.order && !workItemIdsMap.get(Number(id));
            })
            .map(treeItem => treeItem.data.value.coreColumnMap[CoreFieldRefNames.Id] as number);

        if (pos === 1) {
            if (topLevelItemsAfterSelectionIsMoved && topLevelItemsAfterSelectionIsMoved.length > 0) {
                nextWorkItemId = topLevelItemsAfterSelectionIsMoved[0];
            }
        } else {
            const len = topLevelItemsAfterSelectionIsMoved?.length ?? 0;
            // Try to place items on best effort basis. If it's not possible to move to the correct order number, move to the end of the list
            const filteredTopLevelIndex = Math.min(len - 1, pos - 2);
            const topLevelReferencePointWorkItemId = topLevelItemsAfterSelectionIsMoved && topLevelItemsAfterSelectionIsMoved[filteredTopLevelIndex];
            previousWorkItemId = topLevelReferencePointWorkItemId || null;
            if (topLevelItemsAfterSelectionIsMoved && previousWorkItemId) {
                const index = topLevelItemsAfterSelectionIsMoved.indexOf(previousWorkItemId);
                if (index + 1 < topLevelItemsAfterSelectionIsMoved.length) {
                    nextWorkItemId = topLevelItemsAfterSelectionIsMoved[index + 1];
                }
            }
        }

        const itemProvider = backlogTree.value?.itemProvider;
        if (itemProvider) {
            const insertAfterItem = backlogTree.value?.rows.find(row => row.data.id === previousWorkItemId);
            const selectedRows = itemProvider.value.filter(treeProviderItem => workItemIds.indexOf(treeProviderItem.underlyingItem.data.id) !== -1);
            
            // if item exists in current itemProvider - take it, otherwise - create new one based on common ITreeItem
            let insertAfterTreeProviderItem = itemProvider.value.find(treeProviderItem => treeProviderItem.underlyingItem.data.id === insertAfterItem?.data.id);
            if (!insertAfterTreeProviderItem && insertAfterItem) {
                insertAfterTreeProviderItem = { 
                    depth: 0, 
                    parentItem: undefined, 
                    underlyingItem: insertAfterItem 
                };
            }
            contentService.insertItems(selectedRows, insertAfterTreeProviderItem);
        }

        const backlogWorkItemService = pageContext.getService<IBacklogWorkItemService>("IBacklogWorkItemService");
        await backlogWorkItemService.reorderWorkItem(workItemIds, undefined, previousWorkItemId, nextWorkItemId, undefined, true);

        onReorder && onReorder(pos);

        setMoving(false);
        onDismiss(props.onDismiss);
    };

    return (
        <Dialog
            defaultActiveElement=".position-textbox"
            titleProps={{ text: Resources.MoveToPosition }}
            onDismiss={() => onDismiss(props.onDismiss)}
            footerButtonProps={[
                {
                    text: Resources.Cancel,
                    onClick: () => onDismiss(props.onDismiss)
                },
                {
                    text: Resources.OK,
                    onClick: () => moveWorkItem(),
                    disabled: isError || moving,
                    primary: true
                }
            ]}
            showCloseButton={true}
            overlay={moving ? { spinnerLabel: Resources.MovingWorkItems } : undefined}
        >
            <div className="scroll-auto flex-grow">
                <FormItem label={format(Resources.MoveWorkItemsOrderLabel, maximumOrder)} error={isError} message={errorMessage}>
                    <TextField
                        inputClassName="position-textbox"
                        inputType="number"
                        required={true}
                        value={position}
                        onChange={(e, pos) => {
                            setPosition(pos);
                            const num = new Number(pos);
                            if (Number.isNaN(num)) {
                                setIsError(true);
                                setErrorMessage(Resources.PositionNANErrorMessage);
                            }
                            if (num > maximumOrder || num < 1 || !/^[0-9]+$/.test(pos)) {
                                setIsError(true);
                                setErrorMessage(format(Resources.PositionErrorMessage, maximumOrder));
                            } else {
                                setIsError(false);
                                setErrorMessage("");
                            }
                        }}
                    />
                </FormItem>
            </div>
        </Dialog>
    );
}

function onDismiss(dismissCallback?: () => void) {
    if (dismissCallback) {
        dismissCallback();
    }
}